/**
 * Types for the People Targets report — NOS's per-person monthly capacity and
 * target utilization, read live from the Nymbl API Gateway.
 *
 * Two feeds, master and detail: `enable-people-targets` returns the people,
 * `enable-person-periods` returns the selected person's months. The detail feed
 * is per-person because `PersonPeriod` is 4,882 rows across 142 people in dev
 * and the report renders one person at a time. See
 * `docs/nos-gateway-endpoints.md`.
 *
 * Read-only: nothing in this report writes to NOS or to Enable.
 */

/** One person in the report's list — NOS's target schedule for them. */
export interface PeopleTargetPerson {
  /** NOS PersonID, lower-cased so it can key against Enable people. */
  id: string;
  name: string;
  /** Date-only (`YYYY-MM-DD`), or null where NOS has none. */
  startDate: string | null;
  /** Null for a current employee. */
  endDate: string | null;
  /** Full Time / Part Time / Variable, or null where NOS has none. */
  scheduleType: string | null;
  active: boolean;
}

/** One month of one person's targets. */
export interface PersonPeriodTarget {
  /** `YYYYMM`, as NOS numbers its months. The report's sort key. */
  periodNumber: number;
  /** e.g. "January 2026"; falls back to the period number. */
  periodName: string;
  /** Hours. Null means NOS has no capacity for the month — not zero hours. */
  capacityHours: number | null;
  /** A FRACTION (0.95), not a percentage. Null where NOS has none. */
  targetUtilization: number | null;
  /** Whether the month counts toward value-creation utilization. */
  inScopeForVc: boolean;
}

/**
 * Company-wide totals for NOS's CURRENT month, behind the report's tiles.
 *
 * `periodName` is carried because `CurrentPeriod` is NOS's own open period and
 * is not necessarily today's month — dev's flag reads June 2026 as of
 * 2026-09-02 — so the tiles name the period they describe instead of implying
 * "now". A stale flag should be visible, not silent.
 */
export interface PeopleTargetsSummary {
  periodName: string;
  periodNumber: number;
  /** People with a row for the month. The denominator for the tiles. */
  peopleWithTargets: number;
  /** How many of those rows the capacity average actually covers (81 of 88 in dev). */
  withCapacity: number;
  avgCapacity: number | null;
  withTarget: number;
  /** A FRACTION, like every other target in this report. */
  avgTarget: number | null;
  inScopeCount: number;
}

/**
 * `connected: false` means the gateway is unconfigured — an expected
 * deployment state the page explains, not a fault. A gateway that IS
 * configured but failing surfaces as an error instead.
 */
export interface PeopleTargetsReport {
  people: PeopleTargetPerson[];
  /**
   * Null when there is nothing to summarize (NOS has no current month
   * flagged) OR when that one read failed while the list succeeded. Both are
   * "no tiles", never zeroes — a tile reading 0 hours would be a claim.
   */
  summary: PeopleTargetsSummary | null;
  connected: boolean;
}

export interface PersonPeriodsReport {
  periods: PersonPeriodTarget[];
  connected: boolean;
}

/** What an unset NOS value renders as. Never 0 — see the tests. */
export const UNSET = "—";

/**
 * A target fraction as a percentage, keeping any fractional part rather than
 * rounding it away. Every one of the 14 distinct values in dev is a whole
 * percentage today (0, 5, 10, 15, 25, 50, 60, 70, 75, 80, 85, 90, 95, 100), so
 * this is defensive rather than load-bearing — but NOS stores the column as
 * `numeric` and nothing stops a 0.875 arriving, and silently showing it as 88%
 * would misstate someone's target.
 *
 * Null renders as unset, never as 0% — and note that 0% IS a real recorded
 * value here (561 rows in dev), which is exactly why the two must not collapse
 * into the same display.
 */
export function targetPercent(fraction: number | null): string {
  if (fraction === null) return UNSET;
  return `${Number((fraction * 100).toFixed(2))}%`;
}

/**
 * Capacity hours to two decimals, matching NOS's own People Targets screen so
 * the two read the same side by side. Null is unset; 0 is genuinely no hours.
 */
export function formatCapacity(hours: number | null): string {
  if (hours === null) return UNSET;
  return hours.toFixed(2);
}

/** What a sortable table hands the sorters. Mirrors `useTableSort`'s state. */
export interface TargetsSort {
  key: string;
  dir: "asc" | "desc";
}

/**
 * Both sorters put UNSET values last regardless of direction. A row NOS has no
 * value for is missing information rather than a low one, and floating it to
 * the top of an ascending sort pushes the rows someone actually asked to see
 * off the visible part of the panel.
 */
function unsetLast<T>(a: T | null, b: T | null, compare: (a: T, b: T) => number, dir: number) {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return compare(a, b) * dir;
}

const byText = (a: string, b: string) => a.localeCompare(b);
const byNumber = (a: number, b: number) => a - b;

/**
 * The people list, sorted client-side — the whole list is already in hand, so
 * paging it back through the gateway to reorder it would be a wasted call.
 *
 * Every column is sortable, and each falls back to the name so that equal
 * values (one schedule type, one active flag) still come out in a stable,
 * predictable order rather than NOS's.
 */
export function sortPeople(people: PeopleTargetPerson[], sort: TargetsSort): PeopleTargetPerson[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  const byName = (a: PeopleTargetPerson, b: PeopleTargetPerson) => a.name.localeCompare(b.name);

  return [...people].sort((a, b) => {
    switch (sort.key) {
      case "startDate":
        return unsetLast(a.startDate, b.startDate, byText, dir) || byName(a, b);
      case "endDate":
        return unsetLast(a.endDate, b.endDate, byText, dir) || byName(a, b);
      case "scheduleType":
        return unsetLast(a.scheduleType, b.scheduleType, byText, dir) || byName(a, b);
      case "active":
        return (Number(b.active) - Number(a.active)) * dir || byName(a, b);
      default:
        return byName(a, b) * dir;
    }
  });
}

/**
 * One person's months, sorted client-side for the same reason.
 *
 * Ties break chronologically rather than by the sorted column, so sorting by
 * target or scope still reads as a timeline within each group.
 */
export function sortPeriods(
  periods: PersonPeriodTarget[],
  sort: TargetsSort,
): PersonPeriodTarget[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  const byMonth = (a: PersonPeriodTarget, b: PersonPeriodTarget) => a.periodNumber - b.periodNumber;

  return [...periods].sort((a, b) => {
    switch (sort.key) {
      case "capacityHours":
        return unsetLast(a.capacityHours, b.capacityHours, byNumber, dir) || byMonth(a, b);
      case "targetUtilization":
        return unsetLast(a.targetUtilization, b.targetUtilization, byNumber, dir) || byMonth(a, b);
      case "inScopeForVc":
        return (Number(b.inScopeForVc) - Number(a.inScopeForVc)) * dir || byMonth(a, b);
      default:
        return byMonth(a, b) * dir;
    }
  });
}

/** Short month names, index 1-12. Matches how NOS labels a period. */
const MONTHS = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * A date-only value as `M/D/YYYY`, the compact form NOS's own grid uses.
 *
 * Two date columns at `formatDateOnly`'s "Jan 2, 2026" width push the people
 * table wider than its panel, which hides the Active column behind a sideways
 * scroll — the one column you look at.
 *
 * Parsed by splitting the string rather than through `Date`, so a UTC-midnight
 * value can never render as the previous day in a western timezone.
 */
export function formatShortDate(dateOnly: string | null): string {
  if (dateOnly === null) return UNSET;
  const [y, m, d] = dateOnly.split("-").map(Number);
  if (!y || !m || !d) return UNSET;
  return `${m}/${d}/${y}`;
}

/**
 * `202509` → "Sep 2025". Derived from the NUMBER, not from NOS's `PeriodName`,
 * so the label is deterministic and short enough for the detail panel's first
 * column. The full name still rides along as the row's tooltip and is what
 * search matches, so someone typing "September" still finds it.
 *
 * A number that isn't a plausible `YYYYMM` is shown raw rather than guessed at
 * — `Period` also holds year rows numbered `202600` and quarters `202614`.
 */
export function formatPeriodLabel(periodNumber: number): string {
  const year = Math.floor(periodNumber / 100);
  const month = periodNumber % 100;
  const name = MONTHS[month];
  if (month < 1 || month > 12 || name === undefined || name === "") return String(periodNumber);
  return `${name} ${year}`;
}
